import { has, isEmpty, map, transform } from 'lodash';

import { isTableType } from 'constants/chart-roles';

/**
 * Applying sort to column if applicable
 *
 * @param {Object} column - column definition  objects
 * @param {Object} sortModelObject - the sort model object e.g.
 *  {
 *    'foo':  { sort: 'asc', sortedAt: 0 },
 *    'bar':  { sort: 'asc', sortedAt: 1 },
 *    ....
 *  }
 *
 * @return {Object} - possibly modified children collection
 */
const applySortToColumn = (column, sortModelObject) => {
  if (isEmpty(sortModelObject[column.field])) {
    return column;
  }

  const { sort, sortedAt } = sortModelObject[column.field];
  return { ...column, sort, sortedAt };
};

/**
 * Generates sorted columns for tables.
 *
 * This stage produces an updated spec with a columnDefs that have sort properties included.
 *
 * @param {Object} stageData - Object containing documents & an environment object with channels and chartType
 * @returns {Object} - Returns a new stage with the table documents grouped & the dynamic columns added
 */
function sortTableColumns(stageData) {
  const {
    environment: { chartType },
    spec: { columnDefs, meta },
  } = stageData;

  if (!isTableType(chartType)) {
    return {};
  }

  if (isEmpty(meta.sortModel)) {
    return {};
  }

  // transforms the sortModel to an object made of colId keys and value of an object with shape
  // { sort: 'asc', sortedAt: 0 }
  const sortModelObject = transform(
    meta.sortModel,
    (accrued, { colId, sort }, sortedAt) => {
      accrued[colId] = { sort, sortedAt };
    },
    {}
  );

  const modifiedColumnDefs = map(columnDefs, column => {
    if (has(column, 'children')) {
      return {
        ...column,
        children: map(column.children, child =>
          applySortToColumn(child, sortModelObject)
        ),
      };
    }

    return applySortToColumn(column, sortModelObject);
  });

  return {
    spec: {
      ...stageData.spec,
      columnDefs: modifiedColumnDefs,
    },
  };
}

export default {
  input: ['environment.chartType', 'spec'],
  fn: sortTableColumns,
};
